import { useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { Alert, Box, Button, Card, CardContent, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { PageContainer, PageHeading } from '@/components/layout';
import { useCreateApplicationMutation, useGetApplicationsQuery } from '@/features/apps/api/appsApi';
import { logger } from '@/utils/logger';

/**
 * Create New Workspace page
 * Allows users to create a new application/workspace
 *
 * URL: /new-workspace
 */
export const NewWorkspacePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const [createApplication, { isLoading, error }] = useCreateApplicationMutation();

  // Get user's tenant ID from their first app (or from user metadata)
  const { data: apps } = useGetApplicationsQuery();
  const tenantId = apps?.[0]?.tenantId || user?.['https://lingk.io/tenantId'] || '';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const [validationError, setValidationError] = useState('');

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    setValidationError('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setValidationError('Workspace name is required');
      return;
    }

    if (!tenantId) {
      setValidationError('Unable to determine tenant ID. Please contact support.');
      return;
    }

    try {
      const result = await createApplication({
        tenantId,
        data: {
          name: formData.name.trim(),
          description: formData.description.trim(),
        },
      }).unwrap();

      // Navigate to the new workspace
      navigate(`/a/${result.id}/dash`);
    } catch (err) {
      logger.error('Failed to create workspace:', err);
    }
  };

  return (
    <PageContainer>
      <PageHeading
        title="Create New Workspace"
        subtitle="Set up a new workspace to manage your integrations and data flows"
      />

      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Workspace Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                  fullWidth
                  autoFocus
                  error={!!validationError}
                  helperText={validationError || 'A descriptive name for your workspace'}
                  disabled={isLoading}
                />

                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Optional description of your workspace's purpose"
                  disabled={isLoading}
                />

                {error && (
                  <Alert severity="error">
                    Failed to create workspace. Please try again.
                    {error && typeof error === 'object' && 'message' in error && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {String(error.message)}
                      </Typography>
                    )}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => navigate('/')}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    loading={isLoading}
                    disabled={!formData.name.trim()}
                  >
                    Create Workspace
                  </Button>
                </Box>
              </Box>
            </form>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            After creating your workspace, you'll be able to:
          </Typography>
          <Box sx={{ mt: 1, textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
            <Typography variant="body2" color="text.secondary" component="li">
              Configure environments and connectors
            </Typography>
            <Typography variant="body2" color="text.secondary" component="li">
              Create and deploy recipes
            </Typography>
            <Typography variant="body2" color="text.secondary" component="li">
              Manage team members and permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" component="li">
              Monitor executions and analytics
            </Typography>
          </Box>
        </Box>
      </Box>
    </PageContainer>
  );
};
